import { HttpClient } from '@angular/common/http';
import { Injectable, inject, NgZone } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationSettings, UpdateNotificationSettings } from '../models/notification.model';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly swPush = inject(SwPush);
  private readonly ngZone = inject(NgZone);
  private readonly logger = inject(LoggerService);
  private readonly baseUrl = `${environment.apiBaseUrl}/notifications`;

  /**
   * شروع گوش دادن به Push Message های دریافتی از Service Worker.
   * باید یک‌بار در ابتدای اپ (مثلاً در AppComponent یا بعد از لاگین) صدا زده شود.
   */
  startListeningForPushMessages(): void {
    if (!this.swPush.isEnabled) {
      return;
    }

    this.swPush.messages.subscribe((message: object) => {
      this.ngZone.run(() => {
        const notification = message as { notification: { title: string; body: string } };
        const { title, body } = notification.notification;
        this.logger.debug('NotificationService', 'Push message received', { title, body });
        this.showNotification(title, body);
      });
    });

    this.swPush.notificationClicks.subscribe((event: object) => {
      this.ngZone.run(() => {
        const clickEvent = event as { action: string; notification: { url?: string } };
        const url = clickEvent.notification?.url ?? '/';
        this.logger.debug('NotificationService', 'Notification click', { url });
        window.open(url, '_self');
      });
    });
  }

  private showNotification(title: string, body: string): void {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/icons/icon-192x192.png',
      });
    }
  }

  getSettings(): Observable<NotificationSettings> {
    return this.http.get<NotificationSettings>(`${this.baseUrl}/settings`);
  }

  updateSettings(request: UpdateNotificationSettings): Observable<NotificationSettings> {
    return this.http.put<NotificationSettings>(`${this.baseUrl}/settings`, request);
  }

  /**
   * بررسی می‌کند آیا این مرورگر/دستگاه از قبل یک Push Subscription فعال دارد
   * یا نه. باید قبل از نمایش دکمه «فعال‌سازی نوتیفیکیشن» صدا زده شود تا اگر
   * کاربر قبلاً فعال کرده، به‌جای دکمه فعال‌سازی، وضعیت «فعال است» نمایش
   * داده شود و کاربر دوباره مجبور به کلیک نشود.
   */
  async isPushEnabled(): Promise<boolean> {
    if (!this.swPush.isEnabled) return false;

    try {
      const subscription = await firstValueFrom(this.swPush.subscription);
      return subscription !== null;
    } catch {
      return false;
    }
  }

  /**
   * درخواست اجازه از کاربر و ثبت Push Subscription مرورگر در بک‌اند.
   * باید از یک اکشن کاربر (مثل کلیک روی دکمه) صدا زده شود، نه به‌صورت خودکار،
   * چون مرورگرها popup اجازه را در غیر این صورت مسدود می‌کنند.
   */
  async enablePushNotifications(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      this.logger.warn('NotificationService', 'Service Worker / Push API در این مرورگر فعال نیست.');
      return false;
    }

    try {
      const vapidResponse = await firstValueFrom(
        this.http.get<{ publicKey: string }>(`${this.baseUrl}/vapid-public-key`),
      );

      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: vapidResponse.publicKey,
      });

      const subJson = subscription.toJSON();
      await firstValueFrom(
        this.http.post(`${this.baseUrl}/subscriptions`, {
          endpoint: subJson.endpoint,
          p256dh: subJson.keys?.['p256dh'],
          auth: subJson.keys?.['auth'],
        }),
      );

      this.logger.info('NotificationService', 'Push notifications enabled successfully');
      return true;
    } catch (error) {
      this.logger.error('NotificationService', 'خطا در فعال‌سازی نوتیفیکیشن', { error });
      return false;
    }
  }

  async disablePushNotifications(): Promise<void> {
    const subscription = await firstValueFrom(this.swPush.subscription);
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await firstValueFrom(
        this.http.delete(`${this.baseUrl}/subscriptions`, { params: { endpoint } }),
      );
      this.logger.info('NotificationService', 'Push notifications disabled');
    }
  }
}
